/**
 * Membership Plans Configuration
 * 
 * SINGLE SOURCE OF TRUTH for all membership plan features and benefits.
 * This file drives:
 * - Pricing page feature lists
 * - Upgrade prompts and paywalls
 * - Feature gating logic
 * - Settings page plan descriptions
 * 
 * IMPORTANT: All tiers have:
 * - Unlimited messaging (no daily limits)
 * - Unlimited global search
 * - Event hosting, and the ability to sell tickets
 *
 * There is no AI in GrowthSync. Sync suggestions, daily growth tips and the
 * "AI features" line were removed from these lists on 2026-08-18 because the
 * features themselves were deleted (see lib/support/answer-question.ts and
 * lib/ai/strategy-content.ts, both rules and member data now). Never put an AI
 * benefit back on a plan.
 *
 * Free tier gets LESS VOLUME AND VISIBILITY, not fewer features:
 * - 1 hosted event per month (Premium 2, Elite unlimited)
 * - 5 sync requests per day (Premium 25, Elite unlimited)
 * - No priority visibility in Discover
 *
 * This comment previously read "Free tier does NOT have: Event hosting", which was
 * stale and contradicted getFreeExcludedFeatures() lower down in this same file.
 * Hosting is open to every member (see lib/permissions.ts IS_EVENT_HOSTING_OPEN_TO_ALL
 * and lib/events/hosting-limits.ts), deliberately, because GrowthSync earns its 10%
 * on the ticket either way. Corrected 2026-07-30.
 */

import type { SubscriptionPlan } from "@/types";

export const plans: SubscriptionPlan[] = [
  {
    id: "free",
    name: "Free",
    frequency: "free",
    price: 0,
    priceLabel: "£0",
    description: "Trial GrowthSync and meet founders in your city.",
    ctaLabel: "Start for free",
    benefits: [
      "Create and share your business profile",
      "5 sync requests per day",
      "Unlimited messaging",
      "Basic profile analytics (views, sync requests, weekly stats)",
      "Join communities (post and comment)",
      "Discover and attend events",
      "Host 1 event per month, free or ticketed",
      "Unlimited global search",
    ],
    dailyLikesLimit: 5,
    canPostInCommunities: true,
    canHostEvents: true,
    visibilityBoost: false,
    globalDiscovery: true, // All tiers get global search
    prioritySupport: false,
    profileBadge: false,
  },
  {
    id: "premium-monthly",
    name: "Premium",
    frequency: "monthly",
    price: 19,
    priceLabel: "£19 / month",
    description: "Build more of the right relationships with more daily sync requests.",
    ctaLabel: "Upgrade to Premium",
    benefits: [
      "25 sync requests per day",
      "Unlimited messaging",
      "See everyone who has liked you, instead of one at a time",
      "Basic profile analytics (views, sync requests, weekly stats)",
      "Host up to 2 events per month, free or ticketed",
      "Priority visibility in Discover",
      "Unlimited global search",
    ],
    dailyLikesLimit: 25,
    canPostInCommunities: true,
    canHostEvents: true,
    visibilityBoost: true,
    globalDiscovery: true, // All tiers get global search
    prioritySupport: false,
    profileBadge: false,
  },
  {
    id: "premium-yearly",
    name: "Premium",
    frequency: "yearly",
    price: 190,
    priceLabel: "£190 / year",
    description: "Best value: two months on us when billed yearly.",
    ctaLabel: "Go yearly and save",
    benefits: [
      "Everything in Premium Monthly",
      "Two months free each year",
      "Quarterly strategy sessions (a written report built from your own activity)",
    ],
    dailyLikesLimit: 25,
    canPostInCommunities: true,
    canHostEvents: true,
    visibilityBoost: true,
    globalDiscovery: true, // All tiers get global search
    prioritySupport: false,
    profileBadge: false,
  },
  {
    id: "elite-monthly",
    name: "Elite",
    frequency: "monthly",
    price: 49,
    priceLabel: "£49 / month",
    description: "For entrepreneurs who want every GrowthSync advantage.",
    ctaLabel: "Upgrade to Elite",
    benefits: [
      "Unlimited global sync requests",
      "Unlimited event hosting, free or ticketed",
      "Elite-only invite events, hidden from public listings",
      "Priority visibility in Discover",
      "Priority visibility for your events, opportunities and services",
      "Advanced profile analytics (trends, insights and response rate)",
      "Elite badge and verified status",
      "Priority support",
      "Unlimited messaging",
      "Priority waitlist access (jump to front)",
      "Elite-only communities",
      "Early access to new features",
      "Unlimited global search",
    ],
    dailyLikesLimit: "unlimited",
    canPostInCommunities: true,
    canHostEvents: true,
    visibilityBoost: true,
    globalDiscovery: true,
    prioritySupport: true,
    profileBadge: true,
  },
  {
    id: "elite-yearly",
    name: "Elite",
    frequency: "yearly",
    price: 490,
    priceLabel: "£490 / year",
    description: "Save two months and unlock Elite concierge perks.",
    ctaLabel: "Elite Yearly",
    benefits: [
      "Everything in Elite Monthly",
      "Two months free each year (save £98)",
      "Dedicated partner manager (monthly check-ins & quarterly reviews)",
      "Quarterly strategy sessions (a written report built from your own activity)",
    ],
    dailyLikesLimit: "unlimited",
    canPostInCommunities: true,
    canHostEvents: true,
    visibilityBoost: true,
    globalDiscovery: true,
    prioritySupport: true,
    profileBadge: true,
  },
];

/**
 * Get feature list for a plan by tier name
 * Used by pricing page and upgrade prompts
 */
export function getPlanFeatures(tier: "free" | "premium" | "elite"): string[] {
  const plan = plans.find((p) => {
    if (tier === "free") return p.frequency === "free";
    if (tier === "premium") return p.name === "Premium" && p.frequency === "monthly";
    if (tier === "elite") return p.name === "Elite" && p.frequency === "monthly";
    return false;
  });
  
  return plan?.benefits || [];
}

/**
 * Get excluded features for Free tier (shown as crossed out)
 */
export function getFreeExcludedFeatures(): string[] {
  // NOT "Host events". Every member can host, and sell tickets, since GrowthSync
  // earns its 10% on the ticket either way (see lib/permissions.ts). What free
  // members do not get is volume and visibility.
  return [
    "Priority visibility in Discover",
  ];
}

/**
 * Number of plan features shown in compact card layouts.
 * Keep this shared so pricing and checkout stay identical.
 */
export const PLAN_FEATURE_PREVIEW_LIMIT = 8;

/**
 * Get display-ready features for a specific plan.
 * Yearly plans include monthly features + yearly-specific additions.
 */
export function getDisplayBenefitsForPlan(plan?: SubscriptionPlan): string[] {
  if (!plan) return [];

  if (plan.frequency === "yearly") {
    const monthlyPlan = plans.find(
      (p) => p.name === plan.name && p.frequency === "monthly",
    );
    const monthlyFeatures = monthlyPlan?.benefits ?? [];
    const yearlySpecificBenefits = plan.benefits.filter(
      (benefit) => !benefit.toLowerCase().includes("everything in"),
    );

    return [...monthlyFeatures, ...yearlySpecificBenefits];
  }

  return plan.benefits ?? [];
}
